import * as THREE from 'three';

export class GLObjectAPIError extends Error {
    constructor(message, statusCode = null, response = null) {
        super(message);
        this.name = 'GLObjectAPIError';
        this.statusCode = statusCode;
        this.response = response;
    }
}

class GLObjectService {
    constructor(baseURL = 'http://192.168.100.28:8080') {
        this.baseURL = baseURL.replace(/\/$/, '');
        this.isEnabled = true;
    }

    setEnabled(enabled) {
        this.isEnabled = !!enabled;
    }

    getEnabled() {
        return this.isEnabled;
    }

    setBaseURL(url) {
        if (!url || typeof url !== 'string') {
            throw new GLObjectAPIError('Base URL must be a non-empty string');
        }
        this.baseURL = url.replace(/\/$/, '');
    }

    getBaseURL() {
        return this.baseURL;
    }

    async createGLObject(vertices, indices) {
        this._validateInput(vertices, indices);
        const response = await this._post('/createGLObject', { vertices, indices });
        return this._handleResponse(response);
    }

    async createGLObjectWithOffset(vertices, indices, offset) {
        this._validateInput(vertices, indices);
        this._validateOffset(offset);
        const response = await this._post('/createGLObjectWithOffset', { vertices, indices, offset });
        return this._handleResponse(response);
    }

    async deformGum(
        gumVertices,
        gumIndices,
        toothVertices,
        toothIndices,
        toothTranslation,
        barycenter,
        movementType = 0,
        caseId = '-1',
        toothId = -1
    ) {
        if (!this.isEnabled) {
            console.warn(`[GLObjectService] System disabled. Bypassing deformGum`);
            return null;
        }

        this._validateInput(gumVertices, gumIndices);
        this._validateInput(toothVertices, toothIndices);
        
        if (!Array.isArray(toothTranslation) || toothTranslation.length !== 3) {
            throw new GLObjectAPIError('toothTranslation must be an array of 3 numbers');
        }
        if (!Array.isArray(barycenter) || barycenter.length !== 3) {
            throw new GLObjectAPIError('barycenter must be an array of 3 numbers');
        }
        if (!Number.isInteger(movementType) || movementType < -1 || movementType > 4) {
            throw new GLObjectAPIError('movementType must be an integer between -1 and 4');
        }
        if (typeof caseId !== 'string') {
            throw new GLObjectAPIError('caseId must be a string');
        }
        if (!Number.isInteger(toothId)) {
            throw new GLObjectAPIError('toothId must be an integer');
        }

        console.log('=== REQUEST: /deformGum ===');
        console.log('Gum Vertices count:', gumVertices.length / 3);
        console.log('Gum Indices count:', gumIndices.length);
        console.log('Tooth Vertices count:', toothVertices.length / 3);
        console.log('Tooth Indices count:', toothIndices.length);
        console.log('Tooth Translation:', toothTranslation);
        console.log('Barycenter:', barycenter);
        console.log('Movement Type:', movementType);
        console.log('Case ID:', caseId);
        console.log('Tooth ID:', toothId);
        console.log('===========================');

        const response = await this._post('/deformGum', {
            gumVertices,
            gumIndices,
            toothVertices,
            toothIndices,
            toothTranslation,
            barycenter,
            movementType,
            caseId,
            toothId
        });

        console.log('=== RESPONSE: /deformGum ===');
        console.log('Success:', response.success);
        console.log('Object ID:', response.object);
        console.log('nbPoints:', response.nbPoints);
        console.log('nbElements:', response.nbElements);
        console.log('============================');

        return this._handleResponse(response);
    }

    async applyOffsetToMeshKeepIndices(mesh, offset) {
        if (!this.isEnabled) {
            console.warn(`[GLObjectService] System disabled. Bypassing offset application`);
            return mesh;
        }

        this._validateMesh(mesh);
        this._validateOffset(offset);
        
        const { vertices, indices } = this._extractMeshData(mesh);
        const apiResponse = await this.createGLObjectWithOffset(vertices, indices, offset);
        
        return this._updateMeshFromResponseKeepIndices(mesh, apiResponse, indices);
    }

    async createThreeMesh(vertices, indices, material = null) {
        const apiResponse = await this.createGLObject(vertices, indices);
        return this._buildMesh(apiResponse, material);
    }

    async createThreeMeshWithOffset(vertices, indices, offset, material = null) {
        const apiResponse = await this.createGLObjectWithOffset(vertices, indices, offset);
        return this._buildMesh(apiResponse, material);
    }

    _validateInput(vertices, indices) {
        this._validateVertices(vertices);
        this._validateIndices(indices, vertices.length / 3);
    }

    _validateVertices(vertices) {
        if (!Array.isArray(vertices) || vertices.length === 0) {
            throw new GLObjectAPIError('Vertices must be a non-empty array');
        }
        if (vertices.length % 3 !== 0) {
            throw new GLObjectAPIError('Vertices count must be a multiple of 3');
        }
        if (!vertices.every(v => typeof v === 'number' && !isNaN(v))) {
            throw new GLObjectAPIError('All vertices must be valid numbers');
        }
    }

    _validateIndices(indices, vertexCount) {
        if (!Array.isArray(indices) || indices.length === 0) {
            throw new GLObjectAPIError('Indices must be a non-empty array');
        }
        if (indices.length % 3 !== 0) {
            throw new GLObjectAPIError('Indices count must be a multiple of 3');
        }
        if (!indices.every(i => Number.isInteger(i))) {
            throw new GLObjectAPIError('All indices must be integers');
        }
        if (indices.some(i => i < 0 || i >= vertexCount)) {
            throw new GLObjectAPIError(`Indices out of bounds (0-${vertexCount - 1})`);
        }
    }

    _validateOffset(offset) {
        if (typeof offset !== 'number' || isNaN(offset)) {
            throw new GLObjectAPIError('Offset must be a valid number');
        }
    }

    _validateMesh(mesh) {
        if (!mesh || !mesh.geometry) {
            throw new GLObjectAPIError('Invalid mesh: must have a geometry');
        }
    }

    _extractMeshData(mesh) {
        const geometry = mesh.geometry;
        const positions = geometry.attributes.position.array;
        
        const vertices = Array.from(positions);
        
        let indices;
        if (geometry.index) {
            indices = Array.from(geometry.index.array);
        } else {
            indices = Array.from({ length: positions.length / 3 }, (_, i) => i);
        }
        
        console.log('=== EXTRACT MESH DATA ===');
        console.log('Input vertices count:', vertices.length / 3);
        console.log('Input indices count:', indices.length);
        console.log('First 9 vertices:', vertices.slice(0, 9));
        console.log('First 9 indices:', indices.slice(0, 9));
        console.log('========================');
        
        return { vertices, indices };
    }

    _copyMatchingAttributes(oldGeometry, newGeometry) {
        const oldCount = oldGeometry.attributes.position.count;
        const newCount = newGeometry.attributes.position.count;
        
        if (oldCount !== newCount) return;
        
        // Copy UVs if they exist
        if (oldGeometry.attributes.uv) {
            newGeometry.setAttribute('uv', oldGeometry.attributes.uv.clone());
        }
        
        // Copy colors if they exist
        if (oldGeometry.attributes.color) {
            newGeometry.setAttribute('color', oldGeometry.attributes.color.clone());
        }
    }

    _updateMeshFromResponseKeepIndices(mesh, apiResponse, originalIndices) {
        const oldGeometry = mesh.geometry;
        
        console.log('=== UPDATE MESH KEEP INDICES ===');
        console.log('Old geometry vertices:', oldGeometry.attributes.position.count);
        console.log('Original indices count:', originalIndices.length);
        console.log('API response points length:', apiResponse.points?.length);

        const oldPoints = oldGeometry.attributes.position.array;
        if (apiResponse.points && apiResponse.points.length > 3) {
            const newP = Array.isArray(apiResponse.points[0]) ? apiResponse.points.flat() : apiResponse.points;
            console.log('Diff on first vertex:', 
                'Old:', oldPoints[0], oldPoints[1], oldPoints[2], 
                'New:', newP[0], newP[1], newP[2]);

            // Count how many vertices actually moved
            let changedCount = 0;
            let maxDist = 0;
            for (let i = 0; i < oldPoints.length; i += 3) {
                const dx = oldPoints[i] - newP[i];
                const dy = oldPoints[i+1] - newP[i+1];
                const dz = oldPoints[i+2] - newP[i+2];
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                if (dist > 0.0001) changedCount++;
                if (dist > maxDist) maxDist = dist;
            }
            console.log(`Vertices moved: ${changedCount} / ${oldPoints.length / 3}`);
            console.log(`Max displacement: ${maxDist}`);
        }

        
        const newGeometry = new THREE.BufferGeometry();
        
        const points = apiResponse.points;
        let positions;
        if (Array.isArray(points[0])) {
            positions = new Float32Array(points.flat());
        } else {
            positions = new Float32Array(points);
        }
        
        newGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        // Keep original indices
        newGeometry.setIndex(originalIndices);
        
        if (apiResponse.normals?.length > 0) {
            const normalsData = apiResponse.normals;
            let normals;
            if (Array.isArray(normalsData[0])) {
                normals = new Float32Array(normalsData.flat());
            } else {
                normals = new Float32Array(normalsData);
            }
            newGeometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        } else {
            newGeometry.computeVertexNormals();
        }
        
        this._copyMatchingAttributes(oldGeometry, newGeometry);
        
        oldGeometry.dispose();
        mesh.geometry = newGeometry;
        
        mesh.userData.glObjectAddress = apiResponse.object;
        mesh.userData.nbPoints = apiResponse.nbPoints;
        mesh.userData.nbElements = originalIndices.length;
        
        console.log('New geometry vertices:', newGeometry.attributes.position.count);
        console.log('New geometry indices:', newGeometry.index.count);
        console.log('=================================');
        
        return mesh;
    }

    async _post(endpoint, data) {
        if (!this.isEnabled) {
            throw new GLObjectAPIError(`[GLObjectService] System disabled. Request to ${endpoint} blocked.`);
        }

        const url = `${this.baseURL}${endpoint}`;
        
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            
            return await this._parseResponse(response);
        } catch (error) {
            throw this._handleError(error, url);
        }
    }

    async _parseResponse(response) {
        let json;
        try {
            json = await response.json();
        } catch {
            throw new GLObjectAPIError('Invalid JSON response', response.status);
        }
        
        if (!json.success) {
            throw new GLObjectAPIError(json.error || 'API error', response.status, json);
        }
        
        if (!response.ok) {
            throw new GLObjectAPIError(`HTTP ${response.status}`, response.status, json);
        }
        
        return json;
    }

    _handleResponse(response) {
        return {
            object: response.object,
            nbPoints: response.nbPoints,
            nbElements: response.nbElements,
            points: response.points,
            indices: response.indices,
            normals: response.normals,
        };
    }

    _handleError(error, url) {
        if (error instanceof GLObjectAPIError) {
            return error;
        }
        if (error.message?.includes('fetch')) {
            return new GLObjectAPIError(`Network error: Cannot reach ${url}`);
        }
        return new GLObjectAPIError(`Request failed: ${error.message}`);
    }

    _buildMesh(apiResponse, material) {
        const geometry = this._createGeometry(apiResponse);
        const meshMaterial = material || new THREE.MeshStandardMaterial({
            color: 0x808080,
            side: THREE.DoubleSide,
        });
        
        const mesh = new THREE.Mesh(geometry, meshMaterial);
        mesh.userData = {
            glObjectAddress: apiResponse.object,
            nbPoints: apiResponse.nbPoints,
            nbElements: apiResponse.nbElements,
        };
        
        return mesh;
    }

    _createGeometry(apiResponse) {
        const geometry = new THREE.BufferGeometry();
        
        // Ensure positions are flat array
        const points = apiResponse.points;
        let positions;
        if (Array.isArray(points[0])) {
            // points is [[x,y,z], [x,y,z], ...]
            positions = new Float32Array(points.flat());
        } else {
            // points is already flat [x,y,z,x,y,z,...]
            positions = new Float32Array(points);
        }
        
        console.log('=== CREATE GEOMETRY ===');
        console.log('Points format:', Array.isArray(points[0]) ? 'array of arrays' : 'flat array');
        console.log('Positions length:', positions.length);
        console.log('First 9 positions:', Array.from(positions.slice(0, 9)));
        
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        
        if (apiResponse.indices?.length > 0) {
            console.log('Indices count:', apiResponse.indices.length);
            console.log('First 9 indices:', apiResponse.indices.slice(0, 9));
            geometry.setIndex(apiResponse.indices);
        } else {
            console.log('No indices provided');
        }
        
        if (apiResponse.normals?.length > 0) {
            const normalsData = apiResponse.normals;
            let normals;
            if (Array.isArray(normalsData[0])) {
                normals = new Float32Array(normalsData.flat());
            } else {
                normals = new Float32Array(normalsData);
            }
            console.log('Normals provided, count:', normals.length / 3);
            geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        } else {
            console.log('No normals, computing...');
            geometry.computeVertexNormals();
        }
        
        console.log('=======================');
        
        return geometry;
    }
}

const glObjectService = new GLObjectService();
export default glObjectService;
export { GLObjectService };
